export interface FileData {
  file: File;
  base64: string;
  mimeType: string;
}

export enum Step {
  UPLOAD = 'UPLOAD',
  UPLOADING_TO_DRIVE = 'UPLOADING_TO_DRIVE',
  PROCESSING = 'PROCESSING',
  RESULTS = 'RESULTS',
  ERROR = 'ERROR',
}

export type QuestionType =
  | 'objective_exact'
  | 'objective_structured'
  | 'short_constructed'
  | 'extended_constructed'
  | 'diagram_labeling'
  | 'diagram_explanation';

export type ReviewFlagType =
  | 'unclear_handwriting'
  | 'low_transcription_confidence'
  | 'format_mismatch'
  | 'ai_uncertainty'
  | 'diagram_unclear'
  | 'missing_answer'
  | 'wrong_question_number'
  | 'unsupported_claim'
  | 'score_rubric_drift'
  | 'invalid_score_increment'
  | 'rubric_level_mismatch'
  | 'missing_criterion_score'
  | 'unsupported_scoring_claim'
  | 'quantitative_overclaim'
  | 'feedback_not_supported'
  | 'confidence_not_justified';

export interface ReviewFlag {
  type: ReviewFlagType;
  severity: 'low' | 'medium' | 'high';
  message: string;
}

export interface DiagnosticAxes {
  conceptAccuracy: 0 | 1 | 2 | 3;
  conceptCompleteness: 0 | 1 | 2 | 3;
  logicalCoherence: 0 | 1 | 2 | 3;
  scientificExpression: 0 | 1 | 2 | 3;
  representationApplication: 0 | 1 | 2 | 3;
}

export type EvidenceSource = 'raw_answer' | 'unclear_span' | 'handwriting_note';

export interface EvidenceSnippet {
  id: string;
  quote: string;
  source: EvidenceSource;
}

export type EvidenceUncertaintyType =
  | 'illegible_text'
  | 'missing_answer'
  | 'uncertain_question_number'
  | 'handwriting_uncertain';

export interface EvidenceUncertaintyFlag {
  type: EvidenceUncertaintyType;
  message: string;
  evidenceSnippetIds: string[];
}

export interface TranscriptionResult {
  questionNumber: string;
  rawAnswer: string;
  normalizedAnswer: string;
  answerDetected: boolean;
  questionNumberSeen?: string;
  handwritingNotes: string[];
  transcriptionConfidence: number;
  unclearSpans: string[];
  spellingObservations: string[];
  evidenceSnippets: EvidenceSnippet[];
  uncertaintyFlags: EvidenceUncertaintyFlag[];
}

export interface ScoringEvidence {
  matchedKeyPoints: string[];
  missingKeyPoints: string[];
  contradictoryStatements: string[];
  rubricCriteriaMet: string[];
  rubricCriteriaMissed: string[];
  observableEvidence: string[];
}

export type CriterionJudgment = 'met' | 'partial' | 'unmet';

export interface RubricCriterionAssessment {
  criterionId: string;
  criterionText: string;
  judgment: CriterionJudgment;
  supportingEvidenceIds: string[];
  notes: string;
}

export interface RubricLevelMarkRule {
  level: string;
  minMarks: number;
  maxMarks: number;
  allowedMarks: number[];
  evidenceRequirement: string;
}

export interface RubricAlignment {
  questionNumber: string;
  questionType: QuestionType;
  questionText: string;
  expectedAnswerFormat: string;
  formatMatchConfidence: number;
  diagnostics: DiagnosticAxes;
  levelMarkRules: RubricLevelMarkRule[];
  criteria: RubricCriterionAssessment[];
  matchedKeyPoints: string[];
  missingKeyPoints: string[];
  contradictoryStatements: string[];
  supportedClaims: string[];
  unsupportedClaims: string[];
  misconceptions: string[];
}

export interface CriterionScore {
  criterionId: string;
  awardedMarks: number;
  maxMarks: number;
  rationale: string;
}

export interface ScoreDecision {
  awardedMarks: number;
  maxMarks: number;
  rubricLevel: string;
  scoreReason: string;
  idealAnswerExact: string;
  idealAnswerStudentFriendly: string;
  rubricLevelRuleApplied?: string;
  partialCreditRuleApplied?: string;
  scoringMethod: 'deterministic' | 'constrained_ai';
  scoringConfidence: number;
  scoringEvidenceIds: string[];
  criterionScores: CriterionScore[];
}

export interface ScoreAlignmentCheck {
  passed: boolean;
  issues: string[];
}

export interface StudentFeedback {
  whatYouDidWell: string;
  whatToImprove: string;
  tryThisNextTime: string;
  feedbackConfidence: number;
  evidenceIds: string[];
  criterionIds: string[];
  unsupportedStatements: string[];
}

export interface ReliabilityCheck {
  code: ReviewFlagType;
  passed: boolean;
  reason: string;
}

export interface ReliabilityAssessment {
  confidenceLevel: 'high' | 'medium' | 'low';
  confidenceScore: number;
  reviewRequired: boolean;
  reason: string;
  checks: ReliabilityCheck[];
}

export interface TeacherViewSummary {
  conciseExplanation: string;
  keyEvidence: string[];
  nextStep: string;
  uncertaintyNote?: string;
}

export interface QuestionEvaluation {
  questionNumber: string;
  questionType: QuestionType;
  questionText: string;
  expectedAnswerFormat: string;
  formatMatchConfidence: number;
  transcription: TranscriptionResult;
  diagnostics: DiagnosticAxes;
  rubricAlignment: RubricAlignment;
  evidence: ScoringEvidence;
  scoring: ScoreDecision;
  scoreAlignment: ScoreAlignmentCheck;
  feedback: StudentFeedback;
  misconceptions: string[];
  reviewFlags: ReviewFlag[];
  reliability: ReliabilityAssessment;
  teacherView: TeacherViewSummary;
  overallConfidence: number;
}

export type ReportReviewState =
  | 'generated'
  | 'needs_review'
  | 'teacher_editing'
  | 'approved'
  | 'exported';

export interface StudentDiagnosticReport {
  studentName: string;
  gradeLevel: string;
  subject: string;
  pagesUsed: number[];
  groupingReasoning: string;
  teacherReviewRequired: boolean;
  reportConfidence: number;
  reviewState?: ReportReviewState;
  totalScore: number;
  maxTotalScore: number;
  percentage: number;
  studentSummary: {
    strengths: string[];
    areasForGrowth: string[];
    nextLearningSteps: string[];
  };
  teacherSummary: {
    diagnosticOverview: string;
    keyMisconceptions: string[];
    reviewFlags: ReviewFlag[];
  };
  questionEvaluations: QuestionEvaluation[];
}

// Compatibility layer for the current UI while the new diagnostic model is being adopted.
export interface QuestionAnalysis {
  questionNumber: string;
  questionText: string;
  maxPoints: number;
  awardedPoints: number;
  rubricLevel: string;
  studentAnswerSummary: string;
  critique: string;
  evaluationLogic: string;
  rubricReference: string;
  spellingErrors: string[];
}

export interface GradingReport {
  studentName: string;
  gradeLevel: string;
  subject: string;
  totalScore: number;
  maxTotalScore: number;
  percentage: number;
  overallFeedback: string;
  strengths: string[];
  areasForImprovement: string[];
  questionAnalysis: QuestionAnalysis[];
  pagesUsed: number[];
  segregationReasoning?: string;
}

export type RequiredFiles = {
  questionPaper: FileData | null;
  answerKey: FileData | null;
  rubric: FileData | null;
  studentSheet: FileData[];
};

export type Role = 'teacher' | 'student';

export interface User {
  id: string;
  name: string;
  role: Role;
  avatar?: string;
  username: string;
  password: string;
}

export interface FileLinks {
  questionPaper?: string;
  answerKey?: string;
  rubric?: string;
  studentSheet?: string | string[];
  generatedReport?: string;
}

export interface AssessmentRecord {
  id: string;
  studentId: string;
  studentName: string;
  subject: string;
  gradeLevel: string;
  score: number;
  maxScore: number;
  dateGraded: string;
  report: StudentDiagnosticReport;
  fileLinks?: FileLinks;
}

export interface StoredFilePayload {
  fileName: string;
  mimeType: string;
  base64: string;
}

export interface SavedTest {
  id: string;
  name: string;
  gradeLevel: string;
  subject: string;
  createdAt: string;
  questionPaper: StoredFilePayload;
  answerKey: StoredFilePayload;
}

export type DashboardTab = 'overview' | 'tests' | 'analytics';
